import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import * as LDClient from "launchdarkly-js-client-sdk";

import _ from "lodash";
import { useBackupUserFlags, User, useUserFlagsBackup } from "~/hooks/useUser";
import { useAuth } from "~/state/auth";
import { useEnv } from "~/state/env";

const LD_KEY = "launchDarklyFlags";

interface Props {
    children: ReactNode;
}

export interface LDFlags {
    hubspotIntegration?: boolean;
    enableEditor?: boolean;
    enableDatasetEditor?: boolean;
    enableQuickLinkEditor?: boolean;
    enableDemoScriptEditor?: boolean;
    enableNewDemoExperienceV1?: boolean;
    enableNewTestExperienceV1?: boolean;
    enableCustomerSubdomains?: boolean;
    enableCustomerSubdomainsCapture?: boolean;
    enableCustomerSubdomainsSandbox?: boolean;
    enableSandboxTutorial?: boolean;
    enableIntuitDemoFix?: boolean;
    hideDemoPanelFooter?: boolean;
    hideSandboxPanelFooter?: boolean;
    enableDemoControlTotp?: boolean;
    preventLeadsFetching?: boolean;
    sendIntuitToDemoRoute?: boolean;
    openDemoControlsInNewWindow?: boolean;
    enablePresenterMode?: boolean;
    preventOldAppFrameInNewSandboxExperience?: boolean;
    demoScriptFeatureShortcuts?: boolean;
    fixDoubleIframing?: boolean;
    enableCapture?: string[];
    showChromeExtensionAlertMessage?: boolean;
    enableSalespersonEmailNotifications?: boolean;
    enableActivityTracking?: boolean;
    useServerSideMixpanelTracking?: boolean;
    enableNoframing?: boolean;
    enableSellerSandboxLink?: boolean;
    enableUserSandboxQueryParameter?: boolean;
    enablePartnerManagement?: boolean;
    enableAlwaysShowNewUserMedia?: boolean;
    enableAccuratePurchasedLicensesCount?: boolean;
    enableArchiveDeals?: boolean;
    usePreAuthTropicBrevoModal?: boolean;
    enableReadOnlyPartnerDeals?: boolean;
    enableNoframingSandboxes?: boolean;
    enableDatasetGenerator?: boolean;
    enableDatasetGeneratorV2?: boolean;
    enableQuickLinksInPresenterMode?: boolean;
    enablePresenterModeTimer?: boolean;
    enableNoShortcutBanner?: boolean;
    enableRemoveStakeholderAction?: boolean;
    enableBulkInvite?: boolean;
}

export const EDIT_FLAGS_MANUALLY =
    import.meta.env.VITE_MANUAL_LD_FLAGS === "true";

export function createDefaultsFlags(defaults?: Partial<LDFlags>): LDFlags {
    return {
        hubspotIntegration: false,
        enableEditor: false,
        enableDatasetEditor: false,
        enableQuickLinkEditor: false,
        enableDemoScriptEditor: false,
        enableSandboxTutorial: false,
        enableIntuitDemoFix: false,
        hideDemoPanelFooter: false,
        hideSandboxPanelFooter: false,
        enableDemoControlTotp: false,
        preventLeadsFetching: false,
        sendIntuitToDemoRoute: false,
        openDemoControlsInNewWindow: false,
        enablePresenterMode: false,
        preventOldAppFrameInNewSandboxExperience: false,
        demoScriptFeatureShortcuts: false,
        fixDoubleIframing: false,
        enableCapture: [],
        showChromeExtensionAlertMessage: false,
        enableSalespersonEmailNotifications: false,
        enableActivityTracking: false,
        useServerSideMixpanelTracking: false,
        enableNoframing: false,
        enableSellerSandboxLink: false,
        enablePartnerManagement: false,
        enableAccuratePurchasedLicensesCount: false,
        enableArchiveDeals: false,
        usePreAuthTropicBrevoModal: false,
        enableReadOnlyPartnerDeals: false,
        enableNoframingSandboxes: false,
        enableAlwaysShowNewUserMedia: false,
        enableDatasetGenerator: false,
        enableDatasetGeneratorV2: false,
        enableQuickLinksInPresenterMode: false,
        enablePresenterModeTimer: false,
        enableNoShortcutBanner: false,
        enableRemoveStakeholderAction: false,
        enableBulkInvite: false,

        // Up for cleanup flags:
        enableNewDemoExperienceV1: true,
        enableNewTestExperienceV1: true,
        enableCustomerSubdomains: true,
        enableCustomerSubdomainsCapture: true,
        enableCustomerSubdomainsSandbox: true,
        enableUserSandboxQueryParameter: true,
        ...defaults,
    };
}

export const LaunchDarklyContext = createContext<{
    flags: LDFlags;
    ready: boolean;
    setFlags: (flags: LDFlags) => void;
}>({ flags: {}, ready: false, setFlags: () => {} });

export function useLaunchDarkly() {
    return useContext(LaunchDarklyContext);
}

export function useFlags() {
    const { flags, ready, setFlags } = useLaunchDarkly();
    return { flags, ready, setFlags };
}

const tryJSONParse = <T,>(json?: string | null): T | undefined => {
    if (json) {
        try {
            return JSON.parse(json);
        } catch (e) {
            return undefined;
        }
    }
};

const getLocalRecord = (key: string): LDFlags | undefined => {
    const record = localStorage.getItem(key);
    return tryJSONParse(record);
};

function getEnableEditor(flags: LDFlags) {
    return _.some([
        flags.enableQuickLinkEditor,
        flags.enableDemoScriptEditor,
        flags.enableDatasetEditor,
    ]);
}

function getEnableIntuitDemoFix(originalFlags: LDFlags) {
    // We want sendIntuitToDemoRoute to supersede enableIntuitDemoFix.
    // Guarantee that when the new flag is true, the old one is false.
    if (originalFlags.sendIntuitToDemoRoute) {
        return false;
    }
    // sendIntuitToDemoRoute is false, so return whatever this was.
    return originalFlags.enableIntuitDemoFix;
}

const getPseudoFlags = (flags: LDFlags) => {
    return {
        enableEditor: getEnableEditor(flags),
        enableIntuitDemoFix: getEnableIntuitDemoFix(flags),
    };
};

function getLaunchDarklyUserContext(user?: User) {
    const isPartner =
        user?.current_workspace?.deal_salesperson_company_name !==
        user?.current_workspace?.company_name;
    return {
        kind: "user",
        key: user?.user_id || "not-logged-in",
        email: user?.email,
        // NOTE: may need to change lines below to user?.extra_info?.{first,last}Name
        firstName: user?.first_name,
        lastName: user?.last_name,
        anonymous: !user?.user_id,
        vendor: user?.current_workspace?.deal_salesperson_company_name,
        partneredCompany: isPartner
            ? user?.current_workspace?.company_name
            : null,
        salesperson_email: user?.current_workspace?.deal_salesperson_email,
    };
}

interface FlagsData {
    flags: LDFlags;
    ready: boolean;
}

export function LaunchDarklyContextProvider({ children }: Props) {
    const { data: user, loading: isUserLoading } = useAuth();
    const [flagsData, setFlagsData] = useState<FlagsData>({
        ready: false,
        flags: {},
    });

    const ldClient = useRef<LDClient.LDClient>();

    const env = useEnv();
    const clientSideID = env?.LAUNCH_DARKLY_CLIENT_ID || "";
    const ldMockedEnvTestFlags = useMemo(
        () => tryJSONParse<LDFlags>(env?.LAUNCH_DARKLY_CYPRESS_TEST_FLAGS),
        [env?.LAUNCH_DARKLY_CYPRESS_TEST_FLAGS]
    );

    const { refetch: fetchUserFlags } = useUserFlagsBackup({
        enabled: false,
    });
    const { mutate: backupFlags } = useBackupUserFlags();

    const isLocal =
        window.location.hostname.endsWith("local.testbox.com") ||
        // Brevo can't be iframed
        window.location.hostname.startsWith("testbox-local.brevo.com") ||
        window.location.hostname.startsWith("testbox-local.tropicapp.io");

    const changeFlags = (flags: LDFlags) => {
        const camelFlags = _.mapKeys(flags, (v, k) => _.camelCase(k));
        const ldFlagsWithPseudoFlags = {
            ready: true,
            flags: {
                ...camelFlags,
                ...getPseudoFlags(flags),
            },
        };
        setFlagsData(ldFlagsWithPseudoFlags);
        return { camelFlags, allFlags: ldFlagsWithPseudoFlags };
    };

    const backupFlagsIfNecessary = async (flags: LDFlags) => {
        const today = new Date();

        const lastBackedUpAt = user?.last_backed_up_at
            ? new Date(user.last_backed_up_at)
            : undefined;

        const isBackupStale =
            lastBackedUpAt?.toDateString() !== today.toDateString();

        if (isBackupStale) {
            const defaultFlags = createDefaultsFlags();
            const nonDefaultFlags = _.pickBy(
                flags,
                (value, key) =>
                    !_.isEqual(value, defaultFlags[key]) && key in defaultFlags
            );
            backupFlags(nonDefaultFlags);
        }
    };

    const onLaunchDarklyFlagsReady = () => {
        const ldFlags = ldClient.current?.allFlags();
        const { camelFlags } = changeFlags(createDefaultsFlags(ldFlags));
        if (user && ldFlags && !_.isEmpty(ldFlags)) {
            backupFlagsIfNecessary(camelFlags);
        }
    };

    const onLaunchDarklyError = async (...args: any[]) => {
        console.log("LaunchDarkly error detected! ", args);
        const response = await fetchUserFlags();
        changeFlags(createDefaultsFlags(response.data));
    };

    /*
     * TODO (EXP-973) - make the pattern we use to get a fake `enableEditor` flag more generalized
     */
    useEffect(() => {
        if (isUserLoading) {
            return;
        }

        const ldMockedStorageTestFlags = getLocalRecord(LD_KEY);

        const mockedTestFlags =
            ldMockedEnvTestFlags || ldMockedStorageTestFlags;

        if (mockedTestFlags && isLocal) {
            changeFlags(createDefaultsFlags(mockedTestFlags));
        } else if (clientSideID) {
            const context = getLaunchDarklyUserContext(user);
            ldClient.current = LDClient.initialize(clientSideID, context);
            ldClient.current.on("ready", onLaunchDarklyFlagsReady);
            ldClient.current.on("error", onLaunchDarklyError);
        } else if (isLocal) {
            changeFlags(createDefaultsFlags());
        } else {
            if (env) {
                console.error(
                    "Critical error - LaunchDarkly clientId is missing!"
                );
            }
            changeFlags({});
        }
    }, [
        clientSideID,
        user?.user_id,
        isUserLoading,
        isLocal,
        ldMockedEnvTestFlags,
    ]);

    return (
        <LaunchDarklyContext.Provider
            value={{
                flags: flagsData.flags || {},
                ready: flagsData.ready,
                setFlags: changeFlags,
            }}
        >
            <div
                data-testid="launch-darkly"
                data-test-flags-ready={flagsData.ready}
                style={{ display: "none" }}
            />
            {children}
        </LaunchDarklyContext.Provider>
    );
}
